import React, { type ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';

import { ReactComponent as CloseIcon } from '@pkg/assets/icons/v2/close.svg';
import { ReactComponent as DownArrowIcon } from '@pkg/assets/icons/v2/down-arrow.svg';
import { ReactComponent as SearchIcon } from '@pkg/assets/icons/v2/search.svg';
import { YatMagnetism } from '@pkg/components';
import { useMobile } from '@pkg/hooks';

import { countryList, dialCode, getFlags } from '@app/helpers';

import './_Input.scss';

interface YatPhoneNumberInputProps {
    onChange: (phone: string) => void;
}

export const YatPhoneNumberInput = ({ onChange }: YatPhoneNumberInputProps) => {
    const [dropdown, setDropdown] = useState(false);
    const inputWrapperRef = useRef<HTMLDivElement>(null);
    const [searchStr, setSearchStr] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [currentCountry, setCurrentCountry] = useState('US');
    const { isMobile } = useMobile();

    const handleSelectCountry = (code: string) => {
        setCurrentCountry(code);
    };

    const formatPhoneNumber = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        let replaced = '';
        if (value) {
            replaced = value.replace(/[^0-9]/gi, '');
        }
        replaced = replaced.slice(0, 10);
        e.target.value = replaced;
        setPhoneNumber(replaced);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (inputWrapperRef.current && !inputWrapperRef.current.contains(event.target as Node)) {
                setDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [inputWrapperRef]);

    useEffect(() => {
        setDropdown(false);
    }, [currentCountry]);

    useEffect(() => {
        onChange(`${dialCode[currentCountry]}${phoneNumber}`);
    }, [phoneNumber, currentCountry, onChange]);

    const filteredCountryCodes = useMemo(() => {
        return Object.keys(countryList).filter((code) =>
            countryList[code].toLowerCase().includes(searchStr.toLowerCase()),
        );
    }, [searchStr]);

    return (
        <div className="yat-phone-number-input" ref={inputWrapperRef}>
            <div className="yat-phone-number-input__code" onClick={() => setDropdown((drop) => !drop)}>
                <span className="flag">{getFlags(currentCountry)}</span>
                <span className="dial-code">{dialCode[currentCountry]}</span>
                <span className={`arrow ${dropdown ? 'close' : ''}`}>
                    <DownArrowIcon />
                </span>
            </div>
            <input
                type="text"
                value={phoneNumber}
                className="yat-phone-number-input__field"
                onChange={(e) => formatPhoneNumber(e)}
                onFocus={() => setDropdown(false)}
                name="phone"
                id="phone-number-input"
            />
            {dropdown && (
                <div className="yat-phone-number-input__dropdown">
                    {isMobile && (
                        <div className="yat-phone-number-input__dropdown-header">
                            <span className="yat-phone-number-input__dropdown-header__title">
                                Select your country code
                            </span>
                            <YatMagnetism>
                                <div
                                    className={`yat-circle-button yat-phone-number-input__dropdown-header__close`}
                                    onClick={() => setDropdown(false)}
                                >
                                    <CloseIcon />
                                </div>
                            </YatMagnetism>
                        </div>
                    )}
                    <div className="yat-phone-number-input__dropdown-backdrop-wrapper">
                        <div className="yat-phone-number-input__dropdown-backdrop"></div>
                    </div>
                    <div className="yat-phone-number-input__dropdown-input-wrapper">
                        <div className="yat-phone-number-input__dropdown-input-icon">
                            <SearchIcon />
                        </div>
                        <input
                            maxLength={11}
                            type="text"
                            className="yat-phone-number-input__dropdown-input"
                            placeholder="Search"
                            onChange={(e) => setSearchStr(e.target.value)}
                        />
                    </div>
                    <ul className="yat-phone-number-input__dropdown-options">
                        {filteredCountryCodes.map((code) => (
                            <li
                                key={code}
                                className="yat-phone-number-input__dropdown-option"
                                onClick={() => handleSelectCountry(code)}
                            >
                                <span className="flag">{getFlags(code)}</span>
                                <span className="description">
                                    <span className="call-code">{dialCode[code]}</span>
                                    {countryList[code]}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};
